/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useCallback, useMemo } from 'react';
import type { TaxonomyCodeDto } from '@shared/types';
import { TaxonomyBrowser } from './TaxonomyBrowser';

interface BatchTaxonomyPickerProps {
  onDone: (taxonomies: TaxonomyCodeDto[]) => void;
  onCancel: () => void;
  formulaCount: number;
  initialSelected?: TaxonomyCodeDto[];
}

/**
 * Modal for selecting multiple taxonomy codes for batch assignment.
 * Same TaxonomyBrowser experience as single-formula assignment,
 * minus formula details/ingredients/navigation.
 */
export function BatchTaxonomyPicker({ onDone, onCancel, formulaCount, initialSelected }: BatchTaxonomyPickerProps) {
  const [selected, setSelected] = useState<TaxonomyCodeDto[]>(initialSelected ?? []);

  const selectedSetIds = useMemo(() => new Set(selected.map((t) => t.setId)), [selected]);

  const handleSelect = useCallback((taxonomy: TaxonomyCodeDto) => {
    setSelected((prev) => {
      if (prev.some((t) => t.setId === taxonomy.setId)) return prev;
      return [...prev, taxonomy];
    });
  }, []);

  const handleRemove = useCallback((setId: string) => {
    setSelected((prev) => prev.filter((t) => t.setId !== setId));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 py-3" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shrink-0">
          <h2 className="text-base font-bold text-gray-900">
            Select Taxonomy Codes for {formulaCount} Formula{formulaCount > 1 ? 's' : ''}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1.5" title="Esc">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden px-4 py-3 flex flex-col min-h-0">
          {/* Selected taxonomy chips */}
          {selected.length > 0 && (
            <div className="bg-dds-blue-50 border border-dds-blue-200 rounded-lg px-3 py-2 mb-3 shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-dds-blue-700 shrink-0">
                  {selected.length} selected:
                </span>
                {selected.map((t) => (
                  <span
                    key={t.setId}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dds-blue-100 text-dds-blue-700 text-xs font-medium"
                  >
                    {t.code}
                    <button
                      onClick={() => handleRemove(t.setId)}
                      className="text-dds-blue-400 hover:text-dds-blue-700 ml-0.5"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Taxonomy browser — same as single-formula */}
          <div className="flex-1 min-h-0 flex flex-col bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2 shrink-0">
              <h4 className="text-sm font-medium text-gray-700">Browse Taxonomy Codes</h4>
            </div>
            <div className="flex-1 min-h-0">
              <TaxonomyBrowser
                onSelect={handleSelect}
                isSaving={false}
                fillHeight
                excludeSetIds={selectedSetIds}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            onClick={() => onDone(selected)}
            disabled={selected.length === 0}
            className="bg-dds-blue-500 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-dds-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Done Assigning ({selected.length} code{selected.length !== 1 ? 's' : ''})
          </button>
        </div>
      </div>
    </div>
  );
}
